/**
 * Combat log line parsing utilities.
 *
 * Every combat log line from the backend is prefixed with @@eventType@@
 * so the frontend can make rendering decisions based on the tag
 * rather than fragile text-content matching.
 *
 * Extended format (battle viewer):
 *   @@eventType[|key=value...]@@message text
 * e.g. @@charge|round=1|actor=teamOne_12|target=teamTwo_34@@Halvdan charges…
 *
 * Old format is still fully supported: @@eventType@@message text
 */

/** Metadata extracted from the extended tag format. */
export interface CombatEventMeta {
  round?: number;
  /** participantKey of the acting combatant (e.g. "teamOne_12") */
  actor?: string;
  /** participantKey of the target combatant */
  target?: string;
  /** Damage dealt in this event */
  damage?: number;
  [key: string]: string | number | undefined;
}

/** Fully parsed combat log event. */
export interface ParsedCombatEvent {
  eventType: string | null;
  text: string;
  meta: CombatEventMeta;
}

/**
 * Parse the @@eventType[|key=value...]@@ prefix from a combat log line.
 * Returns a ParsedCombatEvent with eventType, human-readable text, and
 * any structured metadata (round, actor, target, damage, …).
 */
export function parseCombatTag(line: string): ParsedCombatEvent {
  // Match: @@<eventType>[|<key=val>...]@@ followed by the rest
  const match = line.match(/^@@([^|@]+)(\|[^@]*)?@@/);
  if (match) {
    const eventType = match[1];
    const meta: CombatEventMeta = {};
    if (match[2]) {
      // Parse pipe-delimited key=value pairs after the event type
      match[2].slice(1).split('|').forEach(pair => {
        const eqIdx = pair.indexOf('=');
        if (eqIdx !== -1) {
          const k = pair.slice(0, eqIdx);
          const raw = pair.slice(eqIdx + 1);
          // Coerce numeric fields
          if (k === 'round' || k === 'damage') {
            const n = Number(raw);
            (meta as Record<string, string | number>)[k] = isNaN(n) ? raw : n;
          } else {
            (meta as Record<string, string | number>)[k] = raw;
          }
        }
      });
    }
    return { eventType, text: line.slice(match[0].length), meta };
  }
  // Legacy untagged lines (old battle reports in DB)
  return { eventType: null, text: line, meta: {} };
}

/** Event types rendered as round dividers. */
export const ROUND_EVENTS = new Set([
  'round_header',
]);

/**
 * Event types filtered OUT of the combat log section and shown separately
 * in the rewards panel. Covers XP, silver, reputation, level-ups, guild XP,
 * title changes, and post-combat info.
 */
export const REWARD_EVENTS = new Set([
  'reward_rep_good',
  'reward_rep_evil',
  'reward_pve_winner',
  'reward_pvp_winner',
  'reward_loser',
  'reward_rep_hospital_good',
  'reward_rep_hospital_evil',
  'reward_rep_kill',
  'levelup',
  'guild_levelup',
]);

/**
 * Legacy fallback: checks the text content for reward indicators.
 * Used for old (pre-tag) battle reports stored in the database.
 */
export function isLegacyRewardLine(text: string): boolean {
  return (
    text.includes('receives') ||
    text.includes('has reached level') ||
    text.toLowerCase().includes('reputation')
  );
}

/**
 * Determine whether a combat log line is a round header.
 * Supports both tagged and legacy formats.
 */
export function isRoundHeader(eventType: string | null, text: string): boolean {
  if (eventType) return ROUND_EVENTS.has(eventType);
  // Legacy: old format was "--- Round N ---"
  return text.startsWith('--- Round');
}

/**
 * Determine whether a combat log line is a reward/info line
 * that should be filtered from the combat log section.
 */
export function isRewardLine(eventType: string | null, text: string): boolean {
  if (eventType) return REWARD_EVENTS.has(eventType);
  return isLegacyRewardLine(text);
}
